import { useState, useEffect } from "react";
import { getApiUrl } from "../utils/api";
const API = getApiUrl();
import { useParams } from "react-router-dom";
import { Heart, Calendar, MapPin, Gift, Users, Mail, Phone, MessageCircle, ExternalLink, MessageSquare, Send, Clock, Shirt, Car, Info, CalendarPlus, ShoppingCart, Plus, Minus, Trash2, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";


export default function PublicWeddingSite() {
  const { slug } = useParams();
  const [site, setSite] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  // Guest list search states
  const [guestSearchName, setGuestSearchName] = useState("");
  const [guestSearchResults, setGuestSearchResults] = useState([]);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [guestConfirmForm, setGuestConfirmForm] = useState({
    companions: 0,
    dietary_restrictions: "",
    message: ""
  });
  const [searchingGuest, setSearchingGuest] = useState(false);
  const [confirmingGuest, setConfirmingGuest] = useState(false);
  const [guestConfirmed, setGuestConfirmed] = useState(false);
  
  const [rsvpForm, setRsvpForm] = useState({
    name: "",
    email: "",
    phone: "",
    guests: 1,
    dietary_restrictions: "",
    message: "",
    attending: true
  });
  const [messageForm, setMessageForm] = useState({
    author_name: "",
    message: ""
  });
  const [submittingRsvp, setSubmittingRsvp] = useState(false);
  const [submittingMessage, setSubmittingMessage] = useState(false);
  const [rsvpSubmitted, setRsvpSubmitted] = useState(false);
  const [messageSubmitted, setMessageSubmitted] = useState(false);
  
  // Shopping cart state
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);

  // Countdown timer
  useEffect(() => {
    if (!site?.wedding_date) return;
    
    const targetDate = new Date(site.wedding_date);
    
    const updateCountdown = () => {
      const now = new Date();
      const diff = targetDate - now;
      
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setCountdown({ days, hours, minutes, seconds });
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(interval);
  }, [site?.wedding_date]);

  // Google Calendar
  const addToGoogleCalendar = () => {
    if (!site?.wedding_date) {
      toast.error("Data do evento não definida");
      return;
    }
    
    const eventDate = new Date(site.wedding_date);
    const startDate = eventDate.toISOString().replace(/-|:|\.\d+/g, '').slice(0, 15) + 'Z';
    
    // End date: 6 hours after start
    const endDate = new Date(eventDate.getTime() + 6 * 60 * 60 * 1000)
      .toISOString().replace(/-|:|\.\d+/g, '').slice(0, 15) + 'Z';
    
    const title = `${site.partner1_name} & ${site.partner2_name}`;
    const location = site.event_info?.address || site.event_location || '';
    const details = `Evento: ${title}\n\nConfira mais detalhes em: ${window.location.href}`;
    
    const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${startDate}/${endDate}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(location)}`;
    
    window.open(calendarUrl, '_blank');
  };

  useEffect(() => {
    const fetchSite = async () => {
      try {
        const res = await fetch(`${API}/sites/slug/${slug}`);
        if (res.ok) {
          const data = await res.json();
          setSite(data);
          
          const giftsRes = await fetch(`${API}/sites/${data.site_id}/gifts?active_only=true`);
          if (giftsRes.ok) {
            const giftsData = await giftsRes.json();
            setGifts(giftsData);
          }

          const messagesRes = await fetch(`${API}/sites/${data.site_id}/guestbook?approved_only=true`);
          if (messagesRes.ok) {
            const messagesData = await messagesRes.json();
            setMessages(messagesData);
          }
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSite();
  }, [slug]);

  const handleRsvpChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRsvpForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleRsvpSubmit = async (e) => {
    e.preventDefault();
    setSubmittingRsvp(true);

    try {
      const res = await fetch(`${API}/sites/${site.site_id}/rsvp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...rsvpForm,
          guests: parseInt(rsvpForm.guests)
        })
      });

      if (res.ok) {
        toast.success("Confirmação enviada com sucesso!");
        setRsvpSubmitted(true);
      } else {
        throw new Error('Erro ao enviar');
      }
    } catch (error) {
      toast.error("Erro ao enviar confirmação");
    } finally {
      setSubmittingRsvp(false);
    }
  };

  const handleGiftClick = (gift) => {
    // Add to cart instead of going directly to checkout
    addToCart(gift);
  };

  const handleDonation = () => {
    window.location.href = `/checkout/${site.site_id}`;
  };

  // Cart functions
  const addToCart = (gift) => {
    const existingItem = cart.find(item => item.gift_id === gift.gift_id);
    if (existingItem) {
      toast.info("Este presente já está no carrinho!");
      setShowCart(true);
      return;
    }
    
    setCart(prev => [...prev, { ...gift, quantity: 1 }]);
    toast.success(`${gift.name} adicionado ao carrinho!`);
  };

  const removeFromCart = (giftId) => {
    setCart(prev => prev.filter(item => item.gift_id !== giftId));
  };

  const updateCartQuantity = (giftId, delta) => {
    setCart(prev => prev.map(item => {
      if (item.gift_id === giftId) {
        const newQty = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const handleCheckoutCart = () => {
    if (cart.length === 0) {
      toast.error("Seu carrinho está vazio!");
      return;
    }
    
    // Create URL with all gifts in cart
    const giftParams = cart.map(item => `gift=${item.gift_id}&qty=${item.quantity}`).join('&');
    window.location.href = `/checkout/${site.site_id}?${giftParams}`;
  };

  const handleMessageSubmit = async (e) => {
    e.preventDefault();
    setSubmittingMessage(true);

    try {
      const res = await fetch(`${API}/sites/${site.site_id}/guestbook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(messageForm)
      });

      if (res.ok) {
        const newMessage = await res.json();
        setMessages(prev => [newMessage, ...prev]);
        toast.success("Mensagem enviada com sucesso!");
        setMessageSubmitted(true);
        setMessageForm({ author_name: "", message: "" });
      } else {
        throw new Error('Erro ao enviar');
      }
    } catch (error) {
      toast.error("Erro ao enviar mensagem");
    } finally {
      setSubmittingMessage(false);
    }
  };

  // Guest list search and confirm functions
  const handleGuestSearch = async () => {
    if (!guestSearchName.trim() || guestSearchName.length < 2) {
      toast.error("Digite pelo menos 2 caracteres para buscar");
      return;
    }
    
    setSearchingGuest(true);
    setGuestSearchResults([]);
    setSelectedGuest(null);
    
    try {
      const res = await fetch(`${API}/sites/${site.site_id}/guest-list/search?name=${encodeURIComponent(guestSearchName)}`);
      if (res.ok) {
        const results = await res.json();
        setGuestSearchResults(results);
        if (results.length === 0) {
          toast.info("Nenhum convidado encontrado com esse nome");
        }
      }
    } catch (error) {
      toast.error("Erro ao buscar convidado");
    } finally {
      setSearchingGuest(false);
    }
  };

  const handleSelectGuest = (guest) => {
    if (guest.confirmed) {
      toast.info("Este convidado já confirmou presença");
      return;
    }
    setSelectedGuest(guest);
    setGuestConfirmForm({
      companions: 0,
      dietary_restrictions: "",
      message: ""
    });
  };

  const handleGuestConfirm = async (e) => {
    e.preventDefault();
    setConfirmingGuest(true);
    
    try {
      const res = await fetch(`${API}/sites/${site.site_id}/guest-list/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guest_name: selectedGuest.name,
          companions: guestConfirmForm.companions,
          dietary_restrictions: guestConfirmForm.dietary_restrictions,
          message: guestConfirmForm.message
        })
      });

      if (res.ok) {
        toast.success("Presença confirmada com sucesso!");
        setGuestConfirmed(true);
        setSelectedGuest(null);
        setGuestSearchResults([]);
        setGuestSearchName("");
        
        // Scroll to top of the page to see photos and gifts
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 1500);
      } else {
        const error = await res.json();
        toast.error(error.detail || "Erro ao confirmar presença");
      }
    } catch (error) {
      toast.error("Erro ao confirmar presença");
    } finally {
      setConfirmingGuest(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('pt-BR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getDaysUntilWedding = () => {
    if (!site?.wedding_date) return null;
    const wedding = new Date(site.wedding_date + 'T12:00:00');
    const today = new Date();
    const diff = wedding - today;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-16 h-16 border-4 border-[#F3C9CF] border-t-[#B3121B] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!site) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="text-center">
          <Heart className="w-16 h-16 text-[#F3C9CF] mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-[#6E0F1A] mb-2">Site não encontrado</h1>
          <p className="text-[#6E0F1A]/60">O site de casamento que você procura não existe.</p>
        </div>
      </div>
    );
  }

  const daysUntil = getDaysUntilWedding();
  const visibleBlocks = site.blocks?.filter(b => b.visible).sort((a, b) => a.order - b.order) || [];

  // Theme colors with fallbacks
  const themeColors = {
    primary: site.theme_colors?.primary || "#B3121B",
    secondary: site.theme_colors?.secondary || "#F3C9CF",
    accent: site.theme_colors?.accent || "#6E0F1A",
    background: site.theme_colors?.background || "#FFFAF5"
  };

  // Theme fonts with fallbacks
  const FONT_MAP = {
    "playfair": "'Playfair Display', serif",
    "cormorant": "'Cormorant Garamond', serif",
    "great-vibes": "'Great Vibes', cursive",
    "lora": "'Lora', serif",
    "dancing-script": "'Dancing Script', cursive",
    "montserrat": "'Montserrat', sans-serif",
    "crimson": "'Crimson Text', serif",
    "josefin": "'Josefin Sans', sans-serif"
  };

  const fontHeading = FONT_MAP[site.theme_fonts?.heading] || "'Playfair Display', serif";
  const fontBody = FONT_MAP[site.theme_fonts?.body] || "'Montserrat', sans-serif";

  // Dynamic styles
  const dynamicStyles = `
    .theme-primary { color: ${themeColors.primary}; }
    .theme-primary-bg { background-color: ${themeColors.primary}; }
    .theme-secondary { color: ${themeColors.secondary}; }
    .theme-secondary-bg { background-color: ${themeColors.secondary}; }
    .theme-accent { color: ${themeColors.accent}; }
    .theme-accent-bg { background-color: ${themeColors.accent}; }
    .theme-bg { background-color: ${themeColors.background}; }
    .theme-heading { font-family: ${fontHeading}; }
    .theme-body { font-family: ${fontBody}; }
    .theme-btn { background-color: ${themeColors.primary}; }
    .theme-btn:hover { background-color: ${themeColors.accent}; }
    .theme-border { border-color: ${themeColors.secondary}; }
    .theme-border-primary { border-color: ${themeColors.primary}; }
  `;

  return (
    <>
      <style>{dynamicStyles}</style>
      <div className="min-h-screen theme-bg theme-body" data-testid="public-wedding-site" style={{ fontFamily: fontBody }}>
      {/* Hero Section */}
      <section 
        className="min-h-screen relative flex items-center justify-center"
        style={{
          backgroundImage: site.cover_image 
            ? `url(${process.env.REACT_APP_BACKEND_URL}${site.cover_image})`
            : `linear-gradient(135deg, ${themeColors.secondary} 0%, ${themeColors.primary}40 100%)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="relative z-10 text-center text-white px-6 py-20 animate-fade-in">
          <p className="uppercase tracking-[0.3em] text-sm mb-6 text-white/80" style={{ fontFamily: fontBody }}>Convidamos você para celebrar</p>
          <h1 className="text-6xl md:text-8xl mb-8" data-testid="couple-names" style={{ fontFamily: fontHeading }}>
            {site.partner1_name}{site.partner2_name ? ` & ${site.partner2_name}` : ''}
          </h1>
          {site.wedding_date && (
            <div className="flex items-center justify-center gap-3 text-xl text-white/90">
              <Calendar className="w-6 h-6" />
              <span>{formatDate(site.wedding_date)}</span>
            </div>
          )}
          {site.event_location && (
            <div className="flex items-center justify-center gap-3 text-lg text-white/80 mt-3">
              <MapPin className="w-5 h-5" />
              <span>{site.event_location}</span>
            </div>
          )}
          {daysUntil && daysUntil > 0 && (
            <div className="mt-12">
              {/* Countdown Timer */}
              <div className="grid grid-cols-4 gap-4 max-w-md mx-auto mb-8">
                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4">
                  <p className="text-4xl md:text-5xl font-bold">{countdown.days}</p>
                  <p className="text-sm text-white/80">Dias</p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4">
                  <p className="text-4xl md:text-5xl font-bold">{countdown.hours}</p>
                  <p className="text-sm text-white/80">Horas</p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4">
                  <p className="text-4xl md:text-5xl font-bold">{countdown.minutes}</p>
                  <p className="text-sm text-white/80">Min</p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4">
                  <p className="text-4xl md:text-5xl font-bold">{countdown.seconds}</p>
                  <p className="text-sm text-white/80">Seg</p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {/* Add to Calendar */}
            <Button
              onClick={addToGoogleCalendar}
              variant="outline"
              className="bg-white/20 backdrop-blur-sm border-white/40 text-white hover:bg-white/30 rounded-full px-6"
              data-testid="add-calendar-btn"
            >
              <CalendarPlus className="w-4 h-4 mr-2" />
              Salvar na Agenda
            </Button>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <Heart className="w-8 h-8 text-white fill-white" />
        </div>
      </section>

      {/* Story Section */}
      {visibleBlocks.find(b => b.type === 'story') && site.story && (
        <section className="py-20 md:py-32 bg-[#FAFAFA] relative">
          <div className="absolute inset-0 noise-overlay"></div>
          <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
            <Heart className="w-12 h-12 text-[#B3121B] mx-auto mb-8" />
            <h2 className="font-serif text-4xl md:text-5xl text-[#6E0F1A] mb-8">Nossa História</h2>
            <p className="text-lg text-[#6E0F1A]/80 leading-relaxed whitespace-pre-line">
              {site.story}
            </p>
          </div>
        </section>
      )}

      {/* Gallery Section */}
      {visibleBlocks.find(b => b.type === 'gallery') && site.gallery_images?.length > 0 && (
        <section className="py-20 md:py-32">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="font-serif text-4xl md:text-5xl text-[#6E0F1A] text-center mb-12">Momentos</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {site.gallery_images.map((img, index) => (
                <img
                  key={index}
                  src={`${process.env.REACT_APP_BACKEND_URL}${img}`}
                  alt={`Gallery ${index + 1}`}
                  className="w-full h-48 md:h-64 object-cover rounded-2xl"
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Event Info Section */}
      {site?.event_info && (site.event_info.arrival_time || site.event_info.ceremony_time || site.event_info.dress_code || site.event_info.address || site.event_info.additional_info || (site.event_info.custom_fields && site.event_info.custom_fields.length > 0)) && (
        <section className="py-20 md:py-32 bg-white relative" id="informacoes">
          <div className="absolute inset-0 noise-overlay"></div>
          <div className="max-w-4xl mx-auto px-6 relative z-10">
            <div className="text-center mb-12">
              <Info className="w-12 h-12 text-[#B3121B] mx-auto mb-6" />
              <h2 className="font-serif text-4xl md:text-5xl text-[#6E0F1A] mb-4">Informações</h2>
              <p className="text-[#6E0F1A]/70 max-w-lg mx-auto">
                Tudo que você precisa saber para o grande dia
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Horários */}
              {(site.event_info.arrival_time || site.event_info.ceremony_time || site.event_info.party_time) && (
                <Card className="card-feature">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-[#F3C9CF]/50 rounded-full flex items-center justify-center">
                        <Clock className="w-5 h-5 text-[#B3121B]" />
                      </div>
                      <h3 className="font-serif text-xl text-[#6E0F1A]">Horários</h3>
                    </div>
                    <div className="space-y-2 text-[#6E0F1A]/80">
                      {site.event_info.arrival_time && (
                        <p><strong>Chegada:</strong> {site.event_info.arrival_time}</p>
                      )}
                      {site.event_info.ceremony_time && (
                        <p><strong>Cerimônia:</strong> {site.event_info.ceremony_time}</p>
                      )}
                      {site.event_info.party_time && (
                        <p><strong>Festa:</strong> {site.event_info.party_time}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Traje */}
              {site.event_info.dress_code && (
                <Card className="card-feature">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-[#CFAFE6]/30 rounded-full flex items-center justify-center">
                        <Shirt className="w-5 h-5 text-[#6E0F1A]" />
                      </div>
                      <h3 className="font-serif text-xl text-[#6E0F1A]">Traje</h3>
                    </div>
                    <p className="text-[#6E0F1A]/80 capitalize">
                      {site.event_info.dress_code.replace(/_/g, ' ')}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Local */}
              {(site.event_info.address || site.event_info.parking_info) && (
                <Card className="card-feature">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-[#567D50]/10 rounded-full flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-[#567D50]" />
                      </div>
                      <h3 className="font-serif text-xl text-[#6E0F1A]">Local</h3>
                    </div>
                    <div className="space-y-3 text-[#6E0F1A]/80">
                      {site.event_info.address && (
                        <p className="whitespace-pre-line">{site.event_info.address}</p>
                      )}
                      {site.event_info.address_link && (
                        <a
                          href={site.event_info.address_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-[#B3121B] hover:underline"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Ver no Google Maps
                        </a>
                      )}
                      {site.event_info.parking_info && (
                        <div className="mt-3 pt-3 border-t border-[#F3C9CF]/30">
                          <div className="flex items-center gap-2 text-sm text-[#6E0F1A]/60 mb-1">
                            <Car className="w-4 h-4" />
                            Estacionamento
                          </div>
                          <p className="text-sm">{site.event_info.parking_info}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Informações Adicionais */}
              {site.event_info.additional_info && (
                <Card className="card-feature">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-[#F3C9CF]/50 rounded-full flex items-center justify-center">
                        <Info className="w-5 h-5 text-[#B3121B]" />
                      </div>
                      <h3 className="font-serif text-xl text-[#6E0F1A]">Informações</h3>
                    </div>
                    <p className="text-[#6E0F1A]/80 whitespace-pre-line">
                      {site.event_info.additional_info}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Campos Personalizados */}
              {site.event_info.custom_fields && site.event_info.custom_fields.length > 0 && site.event_info.custom_fields.map((field, index) => (
                field.label && field.value && (
                  <Card key={index} className="card-feature">
                    <CardContent className="p-6">
                      <h3 className="font-serif text-xl text-[#6E0F1A] mb-3">{field.label}</h3>
                      <p className="text-[#6E0F1A]/80">{field.value}</p>
                    </CardContent>
                  </Card>
                )
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gifts Section */}
      {visibleBlocks.find(b => b.type === 'gifts') && (
        <section className="py-20 md:py-32 bg-[#FAFAFA] relative" id="presentes">
          <div className="absolute inset-0 noise-overlay"></div>
          <div className="max-w-6xl mx-auto px-6 relative z-10">
            <div className="text-center mb-12">
              <Gift className="w-12 h-12 text-[#B3121B] mx-auto mb-6" />
              <h2 className="font-serif text-4xl md:text-5xl text-[#6E0F1A] mb-4">Lista de Presentes</h2>
              <p className="text-[#6E0F1A]/70 max-w-lg mx-auto">
                {site.gifts_message || "Sua presença é o nosso maior presente, mas se desejar nos presentear, escolha um item da nossa lista ou faça uma contribuição livre."}
              </p>
            </div>

            {/* Donation Button */}
            <div className="flex justify-center mb-12">
              <Button
                onClick={handleDonation}
                className="bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full px-8 py-6 text-lg"
                data-testid="donate-btn"
              >
                <Heart className="w-5 h-5 mr-2" />
                Fazer uma Contribuição
              </Button>
            </div>

            {/* Gifts Grid */}
            {gifts.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {gifts.map((gift) => {
                  const progress = gift.price > 0 ? (gift.amount_received / gift.price) * 100 : 0;
                  const quantityTotal = gift.quantity_total || 1;
                  const quantityPurchased = gift.quantity_purchased || 0;
                  const quantityAvailable = quantityTotal - quantityPurchased;
                  const isOutOfStock = quantityAvailable <= 0;
                  const isComplete = progress >= 100 || isOutOfStock;
                  const showProgress = gift.show_progress !== false;
                  const isInCart = cart.some(item => item.gift_id === gift.gift_id);
                  const imageUrl = gift.image_url ? 
                    (gift.image_url.startsWith('http') ? gift.image_url : `${process.env.REACT_APP_BACKEND_URL}${gift.image_url}`) 
                    : null;
                  
                  return (
                    <Card
                      key={gift.gift_id}
                      className={`card-feature overflow-hidden transition-all ${
                        isComplete ? 'opacity-60' : 'cursor-pointer hover:shadow-lg hover:-translate-y-1'
                      } ${isInCart ? 'ring-2 ring-[#567D50]' : ''}`}
                      onClick={() => !isComplete && handleGiftClick(gift)}
                      data-testid={`gift-${gift.gift_id}`}
                    >
                      {imageUrl && (
                        <div className="relative bg-gray-100">
                          <img
                            src={imageUrl}
                            alt={gift.name}
                            className="w-full h-40 object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          {isOutOfStock && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <span className="bg-[#B3121B] text-white px-4 py-2 rounded-full font-semibold text-sm">
                                ESGOTADO
                              </span>
                            </div>
                          )}
                          {isInCart && !isOutOfStock && (
                            <div className="absolute top-2 right-2 bg-[#567D50] text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                              <ShoppingCart className="w-3 h-3" />
                              No carrinho
                            </div>
                          )}
                        </div>
                      )}
                      <CardContent className="p-6">
                        <h3 className="font-semibold text-[#6E0F1A] mb-2">{gift.name}</h3>
                        {gift.description && (
                          <p className="text-sm text-[#6E0F1A]/60 mb-4 line-clamp-2">{gift.description}</p>
                        )}

                        {/* Show "Esgotado" only when out of stock - no quantity shown */}
                        {isOutOfStock && !imageUrl && (
                          <div className="mb-3 text-sm">
                            <span className="text-[#B3121B] font-medium">Esgotado</span>
                          </div>
                        )}
                        
                        {/* Only show progress if enabled AND not out of stock */}
                        {showProgress && !isOutOfStock && (
                          <div className="mb-3">
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-[#6E0F1A]/60">
                                {progress >= 100 ? 'Completo!' : 'Arrecadado'}
                              </span>
                              <span className="text-[#567D50] font-medium">
                                {formatCurrency(gift.amount_received)} / {formatCurrency(gift.price)}
                              </span>
                            </div>
                            <Progress value={Math.min(progress, 100)} className="h-2" />
                          </div>
                        )}
                        
                        {/* Show price only when progress is hidden */}
                        {!showProgress && !isOutOfStock && (
                          <div className="mb-3">
                            <span className="text-[#567D50] font-semibold text-lg">
                              {formatCurrency(gift.price)}
                            </span>
                          </div>
                        )}
                        
                        {!isComplete && (
                          <Button 
                            className={`w-full rounded-full ${
                              isInCart 
                                ? 'bg-[#567D50] hover:bg-[#456840] text-white' 
                                : 'bg-[#B3121B] hover:bg-[#9A0F17] text-white'
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isInCart) {
                                setShowCart(true);
                              } else {
                                handleGiftClick(gift);
                              }
                            }}
                          >
                            {isInCart ? (
                              <>
                                <ShoppingCart className="w-4 h-4 mr-2" />
                                Ver Carrinho
                              </>
                            ) : (
                              <>
                                <Plus className="w-4 h-4 mr-2" />
                                Adicionar
                              </>
                            )}
                          </Button>
                        )}
                        {isOutOfStock && (
                          <div className="text-center text-[#B3121B] font-medium py-2">
                            Esgotado
                          </div>
                        )}
                        {!isOutOfStock && progress >= 100 && showProgress && (
                          <div className="text-center text-[#567D50] font-medium">
                            ✓ Presente já recebido
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* RSVP Section */}
      {visibleBlocks.find(b => b.type === 'rsvp') && (
        <section className="py-20 md:py-32" id="rsvp">
          <div className="max-w-2xl mx-auto px-6">
            <div className="text-center mb-12">
              <Users className="w-12 h-12 text-[#B3121B] mx-auto mb-6" />
              <h2 className="font-serif text-4xl md:text-5xl text-[#6E0F1A] mb-4">Confirme sua Presença</h2>
              <p className="text-[#6E0F1A]/70">
                Ficaremos muito felizes com sua presença. Por favor, confirme até a data do evento.
              </p>
            </div>

            {/* Guest List Search - Busca por nome */}
            {guestConfirmed ? (
              <Card className="card-dashboard text-center py-12 mb-8">
                <CardContent>
                  <div className="w-16 h-16 bg-[#567D50] rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="font-serif text-2xl text-[#6E0F1A] mb-2">Presença Confirmada!</h3>
                  <p className="text-[#6E0F1A]/70">Obrigado por confirmar. Nos vemos em breve!</p>
                  <Button
                    variant="link"
                    onClick={() => setGuestConfirmed(false)}
                    className="text-[#B3121B] mt-4"
                  >
                    Confirmar outro convidado
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="card-dashboard mb-8">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl text-[#6E0F1A] mb-4 text-center">
                    Busque seu nome na lista de convidados
                  </h3>
                  
                  {/* Search Input */}
                  <div className="flex gap-2 mb-6">
                    <Input
                      placeholder="Digite seu nome..."
                      value={guestSearchName}
                      onChange={(e) => setGuestSearchName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleGuestSearch()}
                      className="input-wedding flex-1"
                      data-testid="guest-search-input"
                    />
                    <Button
                      type="button"
                      onClick={handleGuestSearch}
                      disabled={searchingGuest}
                      className="bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full px-6"
                    >
                      {searchingGuest ? "Buscando..." : "Buscar"}
                    </Button>
                  </div>

                  {/* Search Results */}
                  {guestSearchResults.length > 0 && !selectedGuest && (
                    <div className="space-y-2 mb-6">
                      <p className="text-sm text-[#6E0F1A]/60 mb-2">Selecione seu nome:</p>
                      {guestSearchResults.map((guest) => (
                        <div
                          key={guest.guest_id}
                          onClick={() => handleSelectGuest(guest)}
                          className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                            guest.confirmed 
                              ? 'border-gray-200 bg-gray-50 opacity-60' 
                              : 'border-[#F3C9CF] hover:border-[#B3121B] hover:bg-[#F3C9CF]/10'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-[#6E0F1A]">{guest.name}</p>
                              {guest.max_companions > 0 && (
                                <p className="text-sm text-[#6E0F1A]/60">
                                  + {guest.max_companions} acompanhante{guest.max_companions > 1 ? 's' : ''}
                                </p>
                              )}
                            </div>
                            {guest.confirmed ? (
                              <span className="text-sm text-[#567D50] font-medium">✓ Confirmado</span>
                            ) : (
                              <span className="text-sm text-[#B3121B]">Clique para confirmar →</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Confirmation Form */}
                  {selectedGuest && (
                    <form onSubmit={handleGuestConfirm} className="space-y-4 border-t border-[#F3C9CF]/30 pt-6">
                      <div className="bg-[#F3C9CF]/20 rounded-xl p-4 mb-4">
                        <p className="text-[#6E0F1A] font-medium">Confirmando presença de:</p>
                        <p className="text-xl font-serif text-[#B3121B]">{selectedGuest.name}</p>
                      </div>

                      {selectedGuest.max_companions > 0 && (
                        <div>
                          <Label htmlFor="companions" className="text-[#6E0F1A]">
                            Quantos acompanhantes virão? (máximo: {selectedGuest.max_companions})
                          </Label>
                          <Input
                            id="companions"
                            type="number"
                            min="0"
                            max={selectedGuest.max_companions}
                            value={guestConfirmForm.companions}
                            onChange={(e) => setGuestConfirmForm(prev => ({ 
                              ...prev, 
                              companions: Math.min(parseInt(e.target.value) || 0, selectedGuest.max_companions)
                            }))}
                            className="input-wedding mt-2"
                          />
                        </div>
                      )}

                      <div>
                        <Label htmlFor="guest_dietary" className="text-[#6E0F1A]">Restrições Alimentares</Label>
                        <Input
                          id="guest_dietary"
                          value={guestConfirmForm.dietary_restrictions}
                          onChange={(e) => setGuestConfirmForm(prev => ({ ...prev, dietary_restrictions: e.target.value }))}
                          placeholder="Vegetariano, vegano, alergias..."
                          className="input-wedding mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="guest_message" className="text-[#6E0F1A]">Mensagem (opcional)</Label>
                        <Textarea
                          id="guest_message"
                          value={guestConfirmForm.message}
                          onChange={(e) => setGuestConfirmForm(prev => ({ ...prev, message: e.target.value }))}
                          placeholder="Deixe uma mensagem para os anfitriões..."
                          className="input-wedding mt-2"
                          rows={3}
                        />
                      </div>

                      <div className="flex gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setSelectedGuest(null)}
                          className="flex-1 border-[#6E0F1A] text-[#6E0F1A] rounded-full"
                        >
                          Voltar
                        </Button>
                        <Button
                          type="submit"
                          disabled={confirmingGuest}
                          className="flex-1 bg-[#567D50] hover:bg-[#456640] text-white rounded-full"
                        >
                          {confirmingGuest ? "Confirmando..." : "Confirmar Presença"}
                        </Button>
                      </div>
                    </form>
                  )}

                  {/* Alternative: Not on the list */}
                  {!selectedGuest && guestSearchResults.length === 0 && guestSearchName.length >= 2 && (
                    <p className="text-center text-sm text-[#6E0F1A]/50 mt-4">
                      Nome não encontrado na lista. Por favor, verifique a grafia ou entre em contato com os noivos.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </section>
      )}

      {/* Guestbook Section */}
      <section className="py-20 md:py-32 bg-[#FAFAFA] relative" id="recados">
        <div className="absolute inset-0 noise-overlay"></div>
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <MessageSquare className="w-12 h-12 text-[#B3121B] mx-auto mb-6" />
            <h2 className="font-serif text-4xl md:text-5xl text-[#6E0F1A] mb-4">Mural de Recados</h2>
            <p className="text-[#6E0F1A]/70">
              Deixe uma mensagem especial para os noivos!
            </p>
          </div>

          {/* Message Form */}
          {!messageSubmitted ? (
            <Card className="card-dashboard mb-12">
              <CardContent className="p-8">
                <form onSubmit={handleMessageSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="author_name" className="text-[#6E0F1A]">Seu Nome *</Label>
                    <Input
                      id="author_name"
                      value={messageForm.author_name}
                      onChange={(e) => setMessageForm(prev => ({ ...prev, author_name: e.target.value }))}
                      required
                      className="input-wedding mt-2"
                      placeholder="Como você quer ser identificado"
                      data-testid="message-author-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="guestbook_message" className="text-[#6E0F1A]">Sua Mensagem *</Label>
                    <Textarea
                      id="guestbook_message"
                      value={messageForm.message}
                      onChange={(e) => setMessageForm(prev => ({ ...prev, message: e.target.value }))}
                      required
                      className="input-wedding mt-2"
                      placeholder="Escreva uma mensagem de carinho para os noivos..."
                      rows={4}
                      data-testid="message-text-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submittingMessage}
                    className="w-full bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full py-6"
                    data-testid="message-submit-btn"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    {submittingMessage ? 'Enviando...' : 'Enviar Mensagem'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          ) : (
            <Card className="card-dashboard text-center py-8 mb-12">
              <CardContent>
                <div className="w-12 h-12 bg-[#567D50] rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="font-serif text-xl text-[#6E0F1A] mb-2">Mensagem Enviada!</h3>
                <p className="text-[#6E0F1A]/70 text-sm">Obrigado por deixar seu recado.</p>
                <Button
                  variant="link"
                  onClick={() => setMessageSubmitted(false)}
                  className="text-[#B3121B] mt-2"
                >
                  Enviar outra mensagem
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Messages List */}
          {messages.length > 0 && (
            <div className="space-y-4">
              {messages.map((msg) => (
                <Card key={msg.message_id} className="card-dashboard" data-testid={`public-message-${msg.message_id}`}>
                  <CardContent className="py-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#F3C9CF] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="font-semibold text-[#B3121B]">
                          {msg.author_name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#6E0F1A]">{msg.author_name}</h4>
                        <p className="text-[#6E0F1A]/80 mt-1">{msg.message}</p>
                        <p className="text-xs text-[#6E0F1A]/50 mt-2">
                          {new Date(msg.created_at).toLocaleDateString('pt-BR', { 
                            day: '2-digit', 
                            month: 'short', 
                            year: 'numeric' 
                          })}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Floating Cart Button */}
      {cart.length > 0 && (
        <button
          onClick={() => setShowCart(true)}
          className="fixed bottom-6 right-6 bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full p-4 shadow-lg z-50 flex items-center gap-2 transition-all hover:scale-105"
          data-testid="floating-cart-btn"
        >
          <ShoppingCart className="w-6 h-6" />
          <span className="font-semibold">{getCartItemCount()}</span>
          <span className="hidden sm:inline">- {formatCurrency(getCartTotal())}</span>
        </button>
      )}

      {/* Cart Modal */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#6E0F1A] flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Carrinho de Presentes
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto py-4">
            {cart.length === 0 ? (
              <div className="text-center py-8 text-[#6E0F1A]/60">
                <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Seu carrinho está vazio</p>
              </div>
            ) : (
              <div className="space-y-4">
                {cart.map((item) => {
                  const imageUrl = item.image_url ? 
                    (item.image_url.startsWith('http') ? item.image_url : `${process.env.REACT_APP_BACKEND_URL}${item.image_url}`) 
                    : null;
                  
                  return (
                    <div 
                      key={item.gift_id}
                      className="flex items-center gap-4 bg-[#FAFAFA] p-4 rounded-xl"
                      data-testid={`cart-item-${item.gift_id}`}
                    >
                      {imageUrl && (
                        <img
                          src={imageUrl}
                          alt={item.name}
                          className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-[#6E0F1A] truncate">{item.name}</h4>
                        <p className="text-sm text-[#567D50] font-medium">
                          {formatCurrency(item.price)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateCartQuantity(item.gift_id, -1)}
                          className="w-8 h-8 rounded-full bg-[#F3C9CF] text-[#6E0F1A] flex items-center justify-center hover:bg-[#e8b0b8] transition-colors"
                          disabled={item.quantity <= 1}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-medium text-[#6E0F1A]">{item.quantity}</span>
                        <button
                          onClick={() => updateCartQuantity(item.gift_id, 1)}
                          className="w-8 h-8 rounded-full bg-[#F3C9CF] text-[#6E0F1A] flex items-center justify-center hover:bg-[#e8b0b8] transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.gift_id)}
                        className="text-[#B3121B] hover:text-[#9A0F17] p-2"
                        title="Remover"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {cart.length > 0 && (
            <div className="border-t border-[#F3C9CF]/30 pt-4">
              <div className="flex justify-between items-center mb-4">
                <span className="text-[#6E0F1A]/70">Total</span>
                <span className="font-serif text-2xl text-[#6E0F1A]">
                  {formatCurrency(getCartTotal())}
                </span>
              </div>
              <Button
                onClick={handleCheckoutCart}
                className="w-full bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full py-6 text-lg"
                data-testid="checkout-cart-btn"
              >
                Finalizar Presente
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="py-12 text-white text-center" style={{ backgroundColor: themeColors.accent }}>
        <div className="max-w-4xl mx-auto px-6">
          <p className="font-cursive text-4xl mb-4" style={{ fontFamily: fontHeading }}>{site.partner1_name}{site.partner2_name ? ` & ${site.partner2_name}` : ''}</p>
          <p className="text-white/60 text-sm" style={{ fontFamily: fontBody }}>
            Criado com <Heart className="w-4 h-4 inline fill-current" style={{ color: themeColors.primary }} /> usando Fiesta Lab
          </p>
        </div>
      </footer>
    </div>
    </>
  );
}
